import {TextButtonView} from '../../ui_canvas_engine/TextButtonView'
import {CA} from '../../analytics/Analytics'
import {UIGameEndController} from './UIGameEndController'
import {UIGameManager} from '../UIGameManager'
import {TranslationObject} from '@colonist/i18n'
import {Sprites} from '../UIGameImageDictionary'
import {UIGameSpriteHandler} from '../../UIGameSpriteHandler'
import {AnalyticEventCategory, AnalyticEventType} from '../../../../../shared/entity_datas/EntityDatas'
import {ReplayInitiationData} from '@colonist/game-base-types'
import {UIReplayController} from '../../ui_replay/UIReplayController'
import {socket} from '../../EntryShared'
import {UIAPIEndpoints} from '../../network/UIAPIEndpoints'
import {UIAjaxWrapper} from '../../network/UIAjaxWrapper'

export class UIGameEndReturnToMapButtonController {
    private readonly uiGameManager: UIGameManager
    private readonly gameEndController: UIGameEndController
    private readonly viewMapText: TranslationObject
    private readonly viewStatsText: TranslationObject
    private readonly viewReplaySubtext: TranslationObject | undefined

    readonly button: TextButtonView
    private replayDataUnavailable: boolean = false

    // Temporary value to help debug an issue
    private replayDataRequestTime: number | undefined

    constructor(uiGameManager: UIGameManager, x: number, y: number, w: number, h: number, gameEndController: UIGameEndController, viewMapText: TranslationObject, viewStatsText: TranslationObject) {
        this.uiGameManager = uiGameManager
        this.gameEndController = gameEndController
        this.viewMapText = viewMapText
        this.viewStatsText = viewStatsText

        const container = this.uiGameManager.canvasController.app.stage
        this.button = new TextButtonView(x, y, w, h, container)
        this.button.viewContainer.drawRoundedBg(UIGameSpriteHandler.getTextureFromSpritesheet(Sprites.bg_button_hollow_cta))
        this.button.getTextView().removeBold()
        this.button.updateText(viewMapText)
        this.uiGameManager.mapController.setInteractionEnabled(false)
        this.setAction()
    }

    private setAction() {
        this.button.updateAction(() => {
            CA.sendGoogleAnalyticsEvent(CA.Category.EndGame, CA.Label.ClickedEndGameReturnToMap)

            void CA.sendAnalyticEventOnce({
                type: AnalyticEventType.EndGameReturnToMapButton,
                category: AnalyticEventCategory.Game,
            })

            const replayController = this.uiGameManager.chatController.getReplayController()
            if(this.replayDataUnavailable) {
                this.toggleBetweenStatsAndMap()
            } else if(replayController !== undefined) {
                this.toggleBetweenStatsAndReplay(replayController)
            } else {
                void this.initGameEndReplay()
            }
        })
    }

    private toggleBetweenStatsAndMap() {
        const isStatsVisible = !this.gameEndController.isVisible

        this.gameEndController.setVisible(isStatsVisible)
        this.gameEndController.itemBoxController?.setItemBoxVisible(isStatsVisible)

        const isMapVisible = !isStatsVisible
        this.uiGameManager.mapController.setInteractionEnabled(isMapVisible)

        this.updateButton()
    }

    private toggleBetweenStatsAndReplay(replayController: UIReplayController) {
        const isStatsVisible = this.gameEndController.isVisible
        const actionBeforeEndgameScreenIsShown = replayController.actionIndexForEndgame - 1
        const actionIndex = isStatsVisible ? actionBeforeEndgameScreenIsShown : replayController.actionIndexForEndgame
        replayController.setAction(actionIndex, undefined, false)

        this.updateButton()
    }

    updateButton() {
        const isMapVisible = !this.gameEndController.isVisible
        const buttonText = isMapVisible ? this.viewStatsText : this.viewMapText
        const subtext = isMapVisible ? undefined : this.viewReplaySubtext
        this.button.updateText(buttonText, subtext)

        const isOtherOpenStatsButtonActive = this.uiGameManager.chatController.getReplayView()?.openStatsButton !== undefined
        const isVisible = !isOtherOpenStatsButtonActive || !isMapVisible
        this.button.visible = isVisible
    }

    private async initGameEndReplay() {
        this.toggleBetweenStatsAndMap()
        this.button.visible = false

        const replayData = await this.getReplayData()

        if(replayData == undefined) {
            this.replayDataUnavailable = true
            this.updateButton()
            return
        }

        this.startReplay(replayData)
    }

    private async getReplayData(): Promise<ReplayInitiationData | undefined> {
        this.replayDataRequestTime = Date.now()
        const replayDataUrl = UIAPIEndpoints.apiReplayDataFromGameId(socket.gameListener.databaseGameId, this.uiGameManager.gameController.myColor)
        const displayError = false
        const replayDataResponse = await UIAjaxWrapper.getRequestHandler(replayDataUrl, undefined, undefined, displayError)

        if(replayDataResponse == undefined || !replayDataResponse.isSuccess) return undefined
        const replayData = replayDataResponse.json.data as ReplayInitiationData
        replayData.rankedUserStates = this.gameEndController.rankedUserStates

        return replayDataResponse.json.data
    }

    private startReplay(replayData: ReplayInitiationData) {
        // Temporary stat to help track issue with mobile
        if(this.replayDataRequestTime != undefined) {
            const totalReplayDataRequestDelay = Date.now() - this.replayDataRequestTime
            void CA.sendAnalyticEvent({
                category: AnalyticEventCategory.Replay,
                type: AnalyticEventType.EndGameReplayAPIDelay,
                floatValue: totalReplayDataRequestDelay,
            })
        }

        this.uiGameManager.resetGameUIToRebuildOnReconnect()

        // Temporary fix for Discord, overwrite PlayerStates with the ones used in the game since these have the correct discord usernames
        const recentPlayerStates = this.uiGameManager.gameController.getPlayerValidators().map((playerValidator) => playerValidator.userState)
        replayData.playerUserStates = recentPlayerStates

        const isLiveGame = true
        const replayController = new UIReplayController(replayData, socket.actionController, isLiveGame)
        const actionIndexBeforeEndgame = replayController.actionIndexForEndgame - 1
        replayController.setAction(actionIndexBeforeEndgame, 'instant', false)
    }
}
